import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Heading,
  Text,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useCallback, useRef, useState } from "react";
import useComments from "../../hooks/useComments";
import useUser from "../../hooks/useUser";
import { formatDateTime } from "../../lib/number-transforms";
import { Comment } from "../../lib/types/CommentType";
import FormLoadingOverlay from "./FormLoadingOverlay";
import FormSectionHeader from "./FormSectionHeader";

function Discussion() {
  const {
    comments,
    fetchComments,
    deleteComment,
    submitComment,
    commentsLoading,
  } = useComments();
  const { user } = useUser();
  const scrollRef = useRef<any>(null);
  const [comment, setComment] = useState("");
  const usernameText = useColorModeValue("gray.800", "");
  const commentText = useColorModeValue("gray.600", "gray.400");

  const handleInputChange = (e: any) => {
    let inputValue = e.target.value;
    setComment(inputValue);
  };

  const scrollToBottomOfList = () => {
    scrollRef.current.scrollIntoView();
  };

  const updateComments = useCallback(() => {
    fetchComments();
  }, [fetchComments]);

  const submitHelper = useCallback(() => {
    if (!user?.email) return;

    submitComment(comment, user?.email);
    setComment("");
    scrollToBottomOfList();
  }, [comment, user?.email, submitComment]);

  const deleteHelper = useCallback(
    (commentId) => {
      if (!commentId) return;
      deleteComment(commentId);
    },
    [deleteComment]
  );

  return (
    <Box margin="0 auto" maxW="90%">
      {commentsLoading && <FormLoadingOverlay label="Loading..." />}
      <Box display="flex" justifyContent="space-between">
        <FormSectionHeader id="discussion" label="Discussion" />
        <Button
          variant="outline"
          size="sm"
          disabled={commentsLoading}
          onClick={updateComments}
        >
          Update Comments
        </Button>
      </Box>
      {comments.length ? (
        <Box
          mt={2}
          minH="md"
          borderWidth="thin"
          borderStyle="solid"
          px={4}
          py={2}
        >
          {comments.map((comment: Comment) => {
            return (
              <Box key={comment.commentId} maxW="full">
                <Box mt={1} justifyContent="space-between">
                  <Box
                    display="flex"
                    flexDir="row"
                    width="full"
                    justifyContent="space-between"
                  >
                    <Text
                      fontWeight="medium"
                      textColor={usernameText}
                      fontSize="sm"
                      alignSelf="center"
                    >
                      {comment.username}
                    </Text>
                    <Text fontSize="xs">
                      {comment.username === user?.email && (
                        <Button
                          variant="unstyled"
                          color="red.400"
                          fontSize="sm"
                          disabled={commentsLoading}
                          onClick={() => deleteHelper(comment.commentId)}
                        >
                          <DeleteIcon />
                        </Button>
                      )}
                      {formatDateTime(comment.created)}
                    </Text>
                  </Box>
                </Box>
                <Box py={4}>
                  <Text textColor={commentText}>{comment.text}</Text>
                </Box>
                <hr />
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          mt={2}
          minH="md"
          display="flex"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          borderStyle="dashed"
          borderWidth="medium"
        >
          <Heading size="medium" as="h4">
            No Comments
          </Heading>
          <Text mt={2}>There has been no discussion on this quote</Text>
        </Box>
      )}
      <Box justifySelf={"flex-end"}>
        <Textarea
          mt={4}
          value={comment}
          onChange={handleInputChange}
          onKeyDown={(e: any) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (comment.trim().length) {
                submitHelper();
              }
            }
          }}
          disabled={commentsLoading}
          placeholder={"Leave a comment..."}
          size="sm"
          resize="none"
          isInvalid={comment.length >= 256}
          ref={scrollRef}
        />
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Text textColor="gray.500" fontSize="xs">
            {255 - comment.length}/255
          </Text>
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            disabled={
              comment.length >= 256 || commentsLoading || !comment.length
            }
            onClick={submitHelper}
          >
            Post
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default Discussion;
